import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Modal, Space, Typography, Segmented, Slider, Button, Card, Row, Col, Tag, Divider, Radio } from 'antd';
import { CalendarOutlined, GiftOutlined, CreditCardOutlined, WalletOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useApp } from '../context/AppContext';

const { Text } = Typography;

const ExtendSubscriptionModal = ({ visible, onCancel, subscription, onExtend }) => {
  const { user, servers, t } = useApp();
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [selectedBonuses, setSelectedBonuses] = useState(0);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('yookassa');

  // Управление MainButton
  useEffect(() => {
    if (window.Telegram?.WebApp?.MainButton) {
      const mainButton = window.Telegram.WebApp.MainButton;
      
      if (visible) {
        // Скрываем MainButton при открытии модального окна
        mainButton.hide();
      } else {
        // Показываем MainButton при закрытии модального окна (если нужно)
        if (subscription?.status === 'expired') {
          mainButton.setText(t('details_extend_btn'));
          mainButton.show();
        }
      }
    }
  }, [visible, subscription, t]);

  // Находим сервер для текущей подписки
  const server = useMemo(() => {
    if (!servers || !subscription) return null;
    
    // servers может быть объектом с полем servers или массивом напрямую
    const serversList = Array.isArray(servers) ? servers : servers.servers;
    if (!Array.isArray(serversList)) return null;
    
    return serversList.find(s => s.id === subscription.server_id || s.name === subscription.server_name);
  }, [servers, subscription]);

  // Опции периода
  const periodOptions = [
    { label: t('purchase_period_week'), value: 'week', icon: '📅' },
    { label: t('purchase_period_month'), value: 'month', icon: '🗓️' },
    { label: t('purchase_period_year'), value: 'year', icon: '📆' },
  ];

  // Функция расчета базовой цены на основе сервера подписки
  const calculateBasePrice = useCallback(() => {
    if (!selectedPeriod || !server) return 0;
    
    switch (selectedPeriod) {
      case 'week':
        return server.price_week || 0;
      case 'month':
        return server.price_month || 0;
      case 'year':
        return server.price_year || 0;
      default:
        return 0;
    }
  }, [selectedPeriod, server]);

  // Функция расчета итоговой цены с учетом бонусов
  const calculateFinalPrice = useCallback(() => {
    const basePrice = calculateBasePrice();
    const finalPrice = Math.max(0, basePrice - selectedBonuses);
    return {
      basePrice,
      bonusUsed: selectedBonuses,
      finalPrice
    };
  }, [calculateBasePrice, selectedBonuses]);

  // Максимальное количество бонусов которое можно использовать
  const getMaxBonuses = useCallback(() => {
    const basePrice = calculateBasePrice();
    const userBalance = user?.balance || 0;
    return Math.min(userBalance, basePrice);
  }, [calculateBasePrice, user?.balance]);

  const handleBonusChange = useCallback((value) => {
    setSelectedBonuses(value);
  }, []);

  const handleExtend = useCallback(() => {
    if (subscription && onExtend) {
      onExtend({
        subscription_id: subscription.id,
        period: selectedPeriod,
        bonus_used: selectedBonuses,
        payment_method: selectedPaymentMethod
      });
    }
  }, [subscription, selectedPeriod, selectedBonuses, selectedPaymentMethod, onExtend]);

  const resetModal = useCallback(() => {
    setSelectedPeriod('week');
    setSelectedBonuses(0);
    setSelectedPaymentMethod('yookassa');
  }, []);

  const handleCancel = useCallback(() => {
    resetModal();
    onCancel();
  }, [resetModal, onCancel]);

  const changePeriod = useCallback((period) => {
    setSelectedPeriod(period);
    // Сбрасываем бонусы при смене периода, если превышают максимум
    const newMaxBonuses = period === 'week' ? 
      Math.min(user?.balance || 0, server?.price_week || 0) : 
      Math.min(user?.balance || 0, server?.price_month || 0);
    if (selectedBonuses > newMaxBonuses) {
      setSelectedBonuses(newMaxBonuses);
    }
  }, [user?.balance, server, selectedBonuses]);

  // Сброс бонусов при изменении периода
  useEffect(() => {
    const maxBonuses = getMaxBonuses();
    if (selectedBonuses > maxBonuses) {
      setSelectedBonuses(maxBonuses);
    }
  }, [selectedPeriod, getMaxBonuses, selectedBonuses]);

  // Сброс способа оплаты на ЮКасса при выборе недельной подписки
  useEffect(() => {
    if (selectedPeriod === 'week' && selectedPaymentMethod === 'stripe') {
      setSelectedPaymentMethod('yookassa');
    }
  }, [selectedPeriod, selectedPaymentMethod]);

  if (!subscription || !server) {
    return null;
  }

  const priceInfo = calculateFinalPrice();

  // Расчет безопасных отступов из переменных Telegram
  const modalStyle = {
    paddingTop: 'calc(var(--tg-content-safe-area-inset-top, 10px) + var(--tg-safe-area-inset-top, 10px))',
    paddingBottom: 'calc(var(--tg-content-safe-area-inset-bottom, 10px) + var(--tg-safe-area-inset-bottom, 10px))',
  };

  return (
    <Modal
      title={`${t('extend_title')} - ${subscription.server_name || subscription.country}`}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      centered
      width={400}
      style={modalStyle}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        
        {/* Выбор периода */}
        <Card 
          title={
            <Space>
              <CalendarOutlined />
              <span>{t('extend_period')}</span>
            </Space>
          }
          size="small"
        >
          <Segmented
            options={periodOptions.map(option => ({
              label: (
                <Space>
                  <span>{option.icon}</span>
                  <span>{option.label}</span>
                </Space>
              ),
              value: option.value
            }))}
            value={selectedPeriod}
            onChange={changePeriod}
            block
            size="large"
          />
        </Card>

        {/* Блок использования бонусов */}
        {user?.balance > 0 && (
          <Card 
            title={
              <Space>
                <WalletOutlined />
                <span>{t('extend_use_bonuses')}</span>
                <Tag color="green">{user.balance} ₽ {t('extend_available')}</Tag>
              </Space>
            }
            size="small"
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Row justify="space-between" align="middle" style={{ marginBottom: '12px' }}>
                  <Col>
                    <Text type="secondary">{t('purchase_bonuses_deduct')}</Text>
                  </Col>
                  <Col>
                    <Text strong style={{ fontSize: '16px', color: 'var(--tg-theme-button-color, #1677ff)' }}>
                      {selectedBonuses} ₽
                    </Text>
                  </Col>
                </Row>
                
                <Slider
                  min={0}
                  max={getMaxBonuses()}
                  value={selectedBonuses}
                  onChange={handleBonusChange}
                  step={1}
                  tooltip={{
                    formatter: (value) => `${value} ₽`
                  }}
                  trackStyle={{ backgroundColor: 'var(--tg-theme-button-color, #1677ff)' }}
                  handleStyle={{ borderColor: 'var(--tg-theme-button-color, #1677ff)' }}
                  disabled={getMaxBonuses() === 0}
                />
                
                <Row justify="space-between" style={{ marginTop: '8px' }}>
                  <Col>
                    <Text type="secondary" style={{ fontSize: '12px' }}>0 ₽</Text>
                  </Col>
                  <Col>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {getMaxBonuses()} ₽ {t('purchase_bonuses_max')}
                    </Text>
                  </Col>
                </Row>
              </div>
              
              {selectedBonuses > 0 && (
                <Card size="small">
                  <Space>
                    <GiftOutlined style={{ color: 'var(--tg-theme-button-color, #1677ff)' }} />
                    <Text style={{ fontSize: '13px' }}>
                      {t('purchase_bonuses_saving')} <Text strong>{selectedBonuses} ₽</Text> {t('purchase_bonuses_with')}
                    </Text>
                  </Space>
                </Card>
              )}
            </Space>
          </Card>
        )}

        {/* Выбор способа оплаты */}
        <Card 
          title={
            <Space>
              <CreditCardOutlined />
              <span>{t('purchase_payment_title')}</span>
            </Space>
          }
          size="small"
        >
          <Radio.Group 
            value={selectedPaymentMethod} 
            onChange={(e) => setSelectedPaymentMethod(e.target.value)}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Radio value="yookassa" style={{ width: '100%' }}>
                <Space>
                  <span>🇷🇺</span>
                  <div>
                    <Text strong>{t('purchase_yookassa')}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      {t('purchase_yookassa_desc')}
                    </Text>
                  </div>
                </Space>
              </Radio>
              
              <Radio 
                value="stripe" 
                disabled={selectedPeriod === 'week'}
                style={{ 
                  width: '100%',
                  opacity: selectedPeriod === 'week' ? 0.5 : 1
                }}
              >
                <Space>
                  <span>🌍</span>
                  <div>
                    <Text strong>{t('purchase_stripe')}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      {selectedPeriod === 'week' ? t('purchase_stripe_unavailable') : t('purchase_stripe_desc')}
                    </Text>
                  </div>
                </Space>
              </Radio>
            </Space>
          </Radio.Group>
        </Card>

        {/* Сводка тарифа */}
        <Card 
          size="small"
          title={
            <Space>
              <Text strong>
                📋 {t('purchase_summary_title')}
              </Text>
            </Space>
          }
        >
          <Row gutter={[16, 12]}>
            <Col span={12}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space>
                  <ClockCircleOutlined />
                  <Text strong type="primary">{t('purchase_period')}</Text>
                </Space>
                <Space>
                  <span>{periodOptions.find(p => p.value === selectedPeriod)?.icon}</span>
                  <Text strong>
                    {periodOptions.find(p => p.value === selectedPeriod)?.label}
                  </Text>
                </Space>
              </Space>
            </Col>
            
            <Col span={12}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space>
                  <Text strong type="primary">{t('purchase_server')}</Text>
                </Space>
                <Text strong>{server.name}</Text>
              </Space>
            </Col>
            
            <Col span={24}>
              <Divider />
              <Space direction="vertical" size="small" style={{ width: '100%', textAlign: 'center' }}>
                {/* Базовая цена */}
                <Space style={{ width: '100%', justifyContent: 'center' }}>
                  <Text type="secondary">{t('purchase_base_price')}</Text>
                  <Text strong>{priceInfo.basePrice} ₽</Text>
                </Space>
                
                {/* Бонусы к списанию */}
                {priceInfo.bonusUsed > 0 && (
                  <Space style={{ width: '100%', justifyContent: 'center' }}>
                    <Text type="secondary">{t('purchase_bonuses')}</Text>
                    <Text strong style={{ color: '#52c41a' }}>-{priceInfo.bonusUsed} ₽</Text>
                  </Space>
                )}
                
                {/* Итоговая цена */}
                <Space style={{ width: '100%', justifyContent: 'center' }}>
                  <Text strong type="primary" style={{ fontSize: '18px' }}>
                    {t('purchase_total')} {priceInfo.finalPrice} ₽
                  </Text>
                </Space>
                
                {priceInfo.finalPrice === 0 && (
                  <Tag color="success" style={{ marginTop: '8px' }}>
                    {t('purchase_fully_paid')}
                  </Tag>
                )}
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Кнопка продления */}
        <Button
          type="primary"
          size="large"
          icon={<CreditCardOutlined />}
          onClick={handleExtend}
          style={{ width: '100%', height: '50px', fontSize: '16px' }}
        >
          {priceInfo.finalPrice === 0 ? t('extend_btn') : t('purchase_btn_pay')}
        </Button>

      </Space>
    </Modal>
  );
};

export default ExtendSubscriptionModal;
